import { createRouter, createWebHistory } from "vue-router";
import { useStore } from 'vuex';
import store from '@/store';
import axios from 'axios';
import { setApplicationColors } from "../utils/helpers";
import { userCanViewStudentLogs } from "../utils/profileHelpers";

const routes = [
    {
        path: "/",
        redirect: "/login",
        component: () => import("@/views/LoginView.vue"),
        meta: {
            title: "Login",
        },
    },

    {
        path: "/login",
        name: "Login",
        component: () => import("@/views/LoginView.vue"),
        meta: {
            title: "Login",
        },
    },

    {
        path: "/forgot-password",
        name: "Forgot Password",
        component: () => import("@/views/ForgotPasswordView.vue"),
        meta: {
            title: "Recuperação de senha",
        },
    },

    {
        path: "/validate",
        name: "Validate Session",
        component: () => import("@/views/ValidateSessionView.vue"),
        meta: {
            title: "Validação",
        }
    },

    {
        path: "/not-found",
        name: "Not Found",
        component: () => import("@/views/NotFoundView.vue"),
        meta: {
            title: "Não encontrado",
        }
    },

    {
        path: "/error",
        name: "License Error",
        component: () => import("@/views/NoLicenseView.vue"),
        meta: {
            title: "Erro ao acessar",
        }
    },

    {
        path: "/users",
        name: "Users",
        component: () => import("@/views/UsersListView.vue"),
        beforeEnter: async (to, from, next) => {
            const token = store.getters.getToken
            const payload = {
                data: {
                    page: 1,
                    perPage: 30,
                    role: 'users',
                    include: 'avatar'
                },
                userToken: token
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'usersTable', value: true })
                store.dispatch('getUsers', { ...payload, skeletonKey: 'usersTable' })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Usuários",
            requiresAuth: true,
        },
    },

    {
        path: "/users/create",
        name: "Create Users",
        component: () => import("@/views/CreateUserView.vue"),
        meta: {
            title: "Adicionar Usuário",
            requiresAuth: true,
        },
    },

    {
        path: "/users/:id/change-password",
        name: "Change Password",
        component: () => import("@/views/ChangePasswordView.vue"),
        meta: {
            title: "Alterar Senha",
            requiresAuth: true,
        },
    },

    {
        path: "/companies/create",
        name: "Create Companies",
        component: () => import("@/views/CreateCompanyView.vue"),
        meta: {
            title: "Adicionar Empresa",
            requiresAuth: true,
        },
    },

    {
        path: "/companies",
        name: "Companies",
        component: () => import("@/views/CompaniesListView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                data: {
                    status: 'active',
                },
                userToken: userToken
            }

            try {
                await store.dispatch('getCompanies', payload)
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }

        },
        meta: {
            title: "Empresas",
            requiresAuth: true,
        },
    },

    {
        path: "/companies/:id/update",
        name: "Update Company",
        component: () => import("@/views/UpdateCompanyView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                userToken: userToken,
                companyId: to.params.id
            }

            try {
                await store.dispatch('findCompany', payload)
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Atualizar Empresa",
            requiresAuth: true
        }
    },

    {
        path: "/consumers/settings",
        name: "Update Consumer Settings",
        component: () => import("@/views/ConsumerSettingsView.vue"),
        meta: {
            title: "Atualizar dados do consumidor",
            requiresAuth: true
        }
    },

    {
        path: "/home",
        name: "Home",
        component: () => import("@/views/AdminHomePageView.vue"),
        beforeEnter: async (to, from, next) => {
            const token = store.getters.getToken

            try {
                await store.dispatch('getMetrics', { userToken: token })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Início",
            requiresAuth: true
        }
    },

    {
        path: "/integrations",
        name: "Integrations",
        component: () => import("@/views/IntegrationsView.vue"),
        beforeEnter: async (to, from, next) => {
            try {
                await Promise.all([
                    store.dispatch('getIntegrationKeysLTI'),
                    store.dispatch('getIntegrationToolsLTI')
                ])
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }

        },
        meta: {
            title: "Integrações",
            requiresAuth: true
        }
    },

    {
        path: "/my-disciplines",
        name: "My personalized disciplines",
        component: () => import("@/views/ConsumerDisciplinesView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const responsibleId = store.getters.getId
            const payload = {
                data: {
                    perPage: 30,
                    page: 1,
                    status: 'pending'
                },
                userToken: userToken
            }

            const integrationKeysPayload = {
                data: {
                    responsibleId: responsibleId,
                },
                userToken: userToken,
            }

            try {
                await Promise.all([
                    store.dispatch('getIntegrationKeysLTI', integrationKeysPayload),
                    store.dispatch('getConsumerDisciplines', payload)
                ])
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Minhas Disciplinas",
            requiresAuth: true
        }
    },

    {
        path: "/my/disciplines",
        name: "My Disciplines",
        component: () => import("@/views/MyDisciplinesView.vue"),
        beforeEnter: async (to, from, next) => {
            const token = store.getters.getToken
            const userId = store.getters.getId
            const userType = store.getters.getType
            const companyData = store.getters.getCompany

            const payload = {
                userToken: token,
            }

            const warningsPayload = {
                filter: { company_id: companyData.id, status: 'active', perPage: 30 },
                token: token
            }

            const StudentPayload = {
                userToken: token,
                userId: userId,
                type: 'Specific User'
            }

            const disciplinesPayload = {
                data: {
                    perPage: 30
                },
                userToken: token
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'myDisciplinesDisciplineCards', value: true })
                store.dispatch('skeleton/setLoadingState', { key: 'myDisciplinesWarnings', value: true })
                store.dispatch('getDisciplines', { ...disciplinesPayload, skeletonKey: 'myDisciplinesDisciplineCards' })
                store.dispatch('alerts/GetAlerts', { ...warningsPayload, skeletonKey: 'myDisciplinesWarnings' })

                if (userType !== 'student') {
                    store.dispatch('skeleton/setLoadingState', { key: 'myDisciplinesTutorMetrics', value: true })
                    store.dispatch('getTutorMetrics', { ...payload, skeletonKey: 'myDisciplinesTutorMetrics' })
                } else {
                    store.dispatch('skeleton/setLoadingState', { key: 'myDisciplinesStudentData', value: true })
                    store.dispatch('getUserAchievements', { ...StudentPayload, skeletonKey: 'myDisciplinesStudentData' })
                    store.dispatch('getSessionData', { ...StudentPayload, skeletonKey: 'myDisciplinesStudentData' })
                }

                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: "Minhas Disciplinas",
            requiresAuth: true,
        },
    },

    {
        path: "/disciplines",
        name: "Disciplines",
        component: () => import("@/views/DisciplinesView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const disciplinesPayload = {
                userToken: userToken,
                data: {
                    page: 1,
                    perPage: 30,
                    include: 'tags,topics,materials,consumers_in_use'
                }
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'disciplinesViewDisciplineCards', value: true })

                store.dispatch('getSuppliers', userToken)
                store.dispatch('getDisciplineTags', { userToken: userToken })
                store.dispatch('getDisciplines', { ...disciplinesPayload, skeletonKey: 'disciplinesViewDisciplineCards' })

                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Disciplinas",
            requiresAuth: true,
        },
    },

    {
        path: "/discipline/:id/materials",
        name: "Discipline Material",
        component: () => import("@/views/MaterialsDisciplineView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const consumerRole = store.getters.getType
            const materialsPayload = {
                data: {
                    disciplineId: to.params.id
                },
                userToken: userToken
            }

            const disciplinePayload = {
                disciplineId: to.params.id,
                include: 'consumers_in_use',
                userToken: userToken
            }

            const forumsPayload = {
                disciplineId: to.params.id,
                page: 1,
                perPage: 5,
                sort: 'created_at|desc',
                ...consumerRole === 'student' && { visible: true }
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'materialsDisciplineMaterials', value: true })

                await store.dispatch('findDiscipline', disciplinePayload)

                if (consumerRole === 'student') {
                    store.dispatch('getMaterialsReview', to.params.id)
                    store.dispatch('getMaterialAvaliationReasonsInGroups')
                }

                store.dispatch('getForumsData', forumsPayload)
                store.dispatch('getMaterials', { ...materialsPayload, skeletonKey: 'materialsDisciplineMaterials' })

                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Materiais",
            requiresAuth: true,
        },
    },
    {
        path: "/discipline/:id/class",
        name: "Discipline Class",
        component: () => import("@/views/ClassDisciplineView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken

            const payload = {
                data: {
                    disciplineId: to.params.id,
                    page: 1,
                    perPage: 30
                },
                userToken: userToken
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'studentsTable', value: true })

                store.dispatch('getDisciplineInscriptions', { ...payload, skeletonKey: 'studentsTable' })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Turma",
            requiresAuth: true,
        },
    },

    {
        path: "/discipline/:id/grades",
        name: "Discipline Grades",
        component: () => import("@/views/GradesDisciplineView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                data: {
                    disciplineId: to.params.id,
                    include: 'avatar'
                },
                userToken: userToken
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'gradesTable', value: true })
                store.dispatch('getGrades', { ...payload, skeletonKey: 'gradesTable' })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }

        },
        meta: {
            title: "Notas",
            requiresAuth: true,
        },
    },

    {
        path: "/discipline/:id/materials/:materialItemId/quiz",
        name: "Questionnaire",
        component: () => import("@/views/QuestionnaireView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const userType = store.getters.getType
            const userId = store.getters.getId

            const materialItemPayload = {
                materialItemId: to.params.materialItemId,
                userToken: userToken
            }

            const getAttemptsPayload = {
                ...materialItemPayload,
                ...userType === 'student' && { userId: userId }
            }

            try {
                const promises = [
                    store.dispatch('getAttempts', getAttemptsPayload),
                    store.dispatch('findMaterialItem', materialItemPayload)
                ]

                if (userType !== 'student') {
                    promises.push(store.dispatch('getAttempts', materialItemPayload))
                }

                await Promise.all(promises)
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Questionário",
            requiresAuth: true,
        },
    },

    {
        path: "/discipline/:id/materials/:materialItemId/quiz/attempt/:attemptId",
        name: "Questionnaire Attempt",
        component: () => import("@/views/QuestionnaireAttemptView.vue"),
        meta: {
            title: "Questionário",
            requiresAuth: true,
            internalRoute: true
        },
        beforeEnter: async (to, from, next) => {
            const token = store.getters.getToken
            const materialPayload = {
                materialItemId: to.params.materialItemId,
                userToken: token
            }

            const quizzesPayload = {
                data: {
                    materialItemId: to.params.materialItemId
                },
                userToken: token
            }

            const attemptPayload = {
                data: {
                    attemptId: to.params.attemptId,
                    materialItemId: to.params.materialItemId
                },
                userToken: token
            }

            if (to.meta.internalRoute) {
                try {
                    await Promise.all([
                        store.dispatch('findMaterialItem', materialPayload),
                        store.dispatch('getQuizzes', quizzesPayload),
                        store.dispatch('findAttempt', attemptPayload)
                    ])
                    next()
                } catch {
                    next(from)
                    store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
                }
            } else {
                next({ name: "Questionnaire" });
            }
        }
    },

    {
        path: "/discipline/:id/materials/:materialItemId/quiz/attempt/:attemptId/feedback",
        name: "Questionnaire Feedback",
        component: () => import("@/views/QuestionnaireFeedbackView.vue"),
        meta: {
            title: "Feedback do Questionário",
            requiresAuth: true,
            internalRoute: true
        },
        beforeEnter: async (to, from, next) => {
            if (from.name === null) {
                next({ name: "Questionnaire Attempt" });
            } else if (to.meta.internalRoute) {
                const token = store.getters.getToken
                const userAccessedByInternRoutment = !!from.name

                if (userAccessedByInternRoutment) {
                    from.name !== 'Questionnaire Attempt' ? store.dispatch('setRouteThatAccessedFeedbackView', from.path)
                        : store.dispatch('setRouteThatAccessedFeedbackView', `/discipline/${from.params.id}/materials/${from.params.materialItemId}/quiz`)
                }

                const quizzesPayload = {
                    data: {
                        materialItemId: to.params.materialItemId,
                        allowFeedback: true
                    },
                    userToken: token
                }

                const materialPayload = {
                    userToken: token,
                    materialItemId: to.params.materialItemId
                }

                const attemptPayload = {
                    data: {
                        attemptId: to.params.attemptId
                    },
                    userToken: token
                }

                const disciplinePayload = {
                    disciplineId: to.params.id,
                    userToken: token
                }

                try {
                    await Promise.all([
                        store.dispatch('findDiscipline', disciplinePayload),
                        store.dispatch('getQuizzes', quizzesPayload),
                        store.dispatch('findMaterialItem', materialPayload),
                        store.dispatch('findAttempt', attemptPayload)
                    ])
                    next();
                } catch {
                    next(from)
                    store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
                }
            } else {
                next({ name: "Questionnaire Attempt" });
            }
        },
        beforeRouteLeave: async (to, from, next) => {
            await store.dispatch('setRouteThatAccessedFeedbackView')
            next()
        }
    },

    {
        path: "/discipline/:id/materials/:materialItemId/quiz/answers",
        name: "Questionnaire Answers",
        component: () => import("@/views/QuestionnaireAnswersView.vue"),
        meta: {
            title: "Tentativas do Questionário",
            requiresAuth: true,
            internalRoute: true
        },
        beforeEnter: async (to, from, next) => {
            if (to.meta.internalRoute) {
                const token = store.getters.getToken

                const payload = {
                    materialItemId: to.params.materialItemId,
                    userToken: token,
                    role: 'student'
                }

                try {
                    store.dispatch('skeleton/setLoadingState', { key: 'questionnaireAttemptsTable', value: true })

                    await store.dispatch('findMaterialItem', payload)
                    store.dispatch('getAttempts', { ...payload, skeletonKey: 'questionnaireAttemptsTable' })
                    next();
                } catch {
                    next(from)
                    store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
                }
            } else {
                next({ name: "Questionnaire" });
            }
        }
    },

    {
        path: "/quizzes/:id",
        name: "Quizzes",
        component: () => import("@/views/QuizzesView.vue"),
        beforeEnter: async (to, from, next) => {
            const subdomain = window.location.host.split('.')[0]

            const token = store.getters.getToken

            const findDisciplinePayload = {
                disciplineId: to.params.id,
                userToken: token
            }

            const disciplineTopicsPayload = {
                data: {
                    disciplineId: to.params.id,
                    page: 1,
                    perPage: 5
                },
                userToken: token
            }

            const quizzesPayload = {
                data: {
                    disciplineId: to.params.id,
                    page: 1,
                    perPage: 30,
                    sortType: 'id|desc'
                },
                userToken: token
            }

            const requestCompaniesDataPayload = {
                userToken: token,
                domain: subdomain
            }

            try {
                await Promise.all([
                    store.dispatch('getCompanyByConsumer', requestCompaniesDataPayload),
                    store.dispatch('getDisciplineTopics', disciplineTopicsPayload),
                    store.dispatch('findDiscipline', findDisciplinePayload)
                ])
                store.dispatch('getQuizzes', quizzesPayload)
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Banco de Questões",
            requiresAuth: true
        }
    },
    {
        path: "/questions/management",
        name: "Questions Managements",
        component: () => import("@/views/QuestionsManagerView.vue"),
        beforeEnter: async (to, from, next) => {
            try {
                const disciplineRelatedToQuizzes = store.getters.getDisciplineRelatedToQuizzes
                const userToken = store.getters.getToken

                const disciplinePayload = {
                    disciplineId: disciplineRelatedToQuizzes,
                    userToken: userToken
                }

                await store.dispatch('findDiscipline', disciplinePayload)

                next()
            } catch (error) {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Questions",
            requiresAuth: true
        }
    },
    {
        path: "/calendar",
        name: "Calendar View",
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            try {
                await store.dispatch('events/getEventsData', { userToken: userToken })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        component: () => import("@/views/CalendarView.vue"),
        meta: {
            title: "Calendário",
            requiresAuth: true
        }
    },
    {
        path: "/notes",
        name: "Warning View",
        beforeEnter: async (to, from, next) => {
            const token = store.getters.getToken
            const companyId = store.getters.getCompany
            const payload = {
                filter: {
                    company_id: companyId.id
                },
                token: token
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'warningTable', value: true })
                store.dispatch('alerts/GetAlerts', { ...payload, skeletonKey: 'warningTable' })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        component: () => import("@/views/WarningView.vue"),
        meta: {
            title: "Avisos",
            requiresAuth: true
        }
    },
    {
        path: '/dashboard',
        name: 'Student DashBoard',
        component: () => import('@/views/StudentDashBoardView.vue'),
        beforeEnter: async (from, to, next) => {
            const token = store.getters.getToken
            const userId = store.getters.getId

            const payload = {
                userToken: token,
                userId: userId,
                type: 'Specific User',
                skeletonKey: 'studentBadges'
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'studentBadges', value: true })
                store.dispatch('getUserAchievements', payload)
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: 'Dashboard',
            requiresAuth: true
        }
    },

    {
        path: "/tutor/dashboard",
        name: "Tutor DashBoard",
        beforeEnter: async (to, from, next) => {
            const token = store.getters.getToken
            const tutorId = store.getters.getId
            const studentsListPayload = {
                userToken: token,
                data: {
                    tutorId: tutorId,
                    role: 'student',
                    page: 1,
                    perPage: 5,
                }
            }

            const studentsDataPayload = {
                type: 'Students Data For Tutors',
                userToken: token,
                tutorId: tutorId,
                perPage: 20,
                page: 1
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'tutorStudentsTable', value: true })
                store.dispatch('getUsers', studentsListPayload)
                store.dispatch('getSessionData', { ...studentsDataPayload, skeletonKey: 'tutorStudentsTable' })
                store.dispatch('fetchUserAlertCriteria')
                next()
            } catch {
                next(false)
            }

        },
        component: () => import("@/views/TutorDashBoardView.vue"),
        meta: {
            title: 'Dashboard',
            requiresAuth: true
        }
    },
    {
        path: '/discipline/:disciplineId/materials/assignment/:id/management',
        name: 'Assignment Management',
        component: () => import('@/views/AssignmentManagementView.vue'),
        beforeEnter: async (to, from, next) => {
            const userType = store.getters.getType
            const userToken = store.getters.getToken
            const userId = store.getters.getId

            const materialItemPayload = {
                userToken: userToken,
                materialItemId: to.params.id
            }

            const disciplinePayload = {
                userToken: userToken,
                disciplineId: to.params.disciplineId
            }

            const attemptPayload = {
                userToken: userToken,
                materialItemId: to.params.id,
                ...userType === 'student' && { userId: userId }
            }

            const personalAttempts = {
                ...materialItemPayload,
                userId: userId
            }

            try {
                await Promise.all([
                    store.dispatch('findDiscipline', disciplinePayload),
                    store.dispatch('findMaterialItem', materialItemPayload),
                    store.dispatch('getAttempts', attemptPayload),
                    store.dispatch('getPersonalAttempts', personalAttempts)
                ])
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: "Gerenciamento Tarefa",
            requiresAuth: true
        }
    },
    {
        path: '/discipline/:disciplineId/materials/assignment/:materialItemId/attempt/:id',
        name: 'Assigment Attempt',
        component: () => import('@/views/AssigmentAttemptView.vue'),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken

            const materialPayload = {
                materialItemId: to.params.materialItemId,
                userToken: userToken
            }

            const attemptPayload = {
                data: {
                    attemptId: to.params.id,
                    materialItemId: to.params.materialItemId
                },
                userToken: userToken
            }

            try {
                await Promise.all([
                    store.dispatch('findMaterialItem', materialPayload),
                    store.dispatch('findAttempt', attemptPayload)
                ])
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: "Tarefa",
            requiresAuth: true
        }
    },
    {
        path: '/discipline/:disciplineId/assigment/:id/overview',
        name: 'Assigment Overview',
        component: () => import('@/views/AssigmentAttemptOverviewView.vue'),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken

            const attemptPayload = {
                userToken: userToken,
                materialItemId: to.params.id,
                role: 'student'
            }

            const disciplinePayload = {
                userToken: userToken,
                disciplineId: to.params.disciplineId
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'assignmentAttemptTable', value: true })

                await store.dispatch('findDiscipline', disciplinePayload)
                store.dispatch('getAttempts', { ...attemptPayload, skeletonKey: 'assignmentAttemptTable' })
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: "Resumo Tarefa",
            requiresAuth: true
        }
    },
    {
        path: '/discipline/:disciplineId/assigment/:materialItemId/feedback/:attemptId',
        name: "Attempt Feedback",
        component: () => import('@/views/AssigmentFeedbackView.vue'),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken

            const attemptPayload = {
                userToken: userToken,
                data: {
                    attemptId: to.params.attemptId
                }
            }

            const materialPayload = {
                materialItemId: to.params.materialItemId,
                userToken: userToken
            }

            try {
                const userEnteredByInternRoute = !!from.name
                if (userEnteredByInternRoute) {
                    store.dispatch('setLastRouteBeforeAssigmentFeedback', from.path)
                }

                await Promise.all([
                    store.dispatch('findAttempt', attemptPayload),
                    store.dispatch('findMaterialItem', materialPayload)
                ])
                next()
            } catch {
                next(from)
            }
        },
        beforeRouteLeave: async (to, from, next) => {
            await store.dispatch('setLastRouteBeforeAssigmentFeedback')
            next()
        },
        meta: {
            title: "Avaliação Tarefa",
            requiresAuth: true
        }
    },
    {
        path: '/discipline/:disciplineId/forum-management',
        name: 'Forum Management',
        component: () => import("@/views/ForumManagementView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const disciplinePayload = {
                userToken: userToken,
                disciplineId: to.params.disciplineId
            }

            try {
                await store.dispatch('findDiscipline', disciplinePayload)
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: 'Fórum',
            requiresAuth: true
        }
    },
    {
        path: '/discipline/:disciplineId/forum/:forumId',
        name: 'Forum View',
        component: () => import("@/views/ForumView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const userRole = store.getters.getType
            const userId = store.getters.getId

            const disciplinePayload = {
                userToken: userToken,
                disciplineId: to.params.disciplineId
            }

            const commentsPayload = {
                forumId: to.params.forumId,
                numChildren: 10
            }

            const reverbPayload = {
                userRole: userRole,
                forumId: to.params.forumId
            }

            try {
                await Promise.all([
                    store.dispatch('findDiscipline', disciplinePayload),
                    store.dispatch('getForum', to.params.forumId),
                    store.dispatch('getCommentsRelatedToForum', commentsPayload),
                    store.dispatch('listenToForumUpdate', reverbPayload)
                ])
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: 'Fórum',
            requiresAuth: true
        }
    },
    {
        path: '/profile/:id',
        name: 'Profile View',
        component: () => import('@/views/ProfileView.vue'),
        meta: {
            title: 'Perfil',
            requiresAuth: true
        }
    },
    {
        path: '/profile/:id/settings',
        name: "Profile Settings",
        component: () => import('@/views/ProfileSettingsView.vue'),
        beforeEnter: async (to, from, next) => {
            const userId = store.getters.getId
            const userCanAccessRoute = userId == to.params.id

            !userCanAccessRoute && (next(from))

            try {

                const findUserPayload = {
                    userId: userId,
                    updateMainUserInStore: true,
                    include: 'avatar'
                }

                await Promise.all([
                    store.dispatch('findUser', findUserPayload),
                    store.dispatch('getUserContacts', userId)
                ])
                next()
            } catch {
                next(from)
            }
        },
        meta: {
            title: 'Configurações Perfil',
            requiresAuth: true
        }
    },
    {
        path: '/disciplines/feedback',
        name: 'Disciplines Feedback',
        component: () => import('@/views/DisciplinesFeedbackView.vue'),
        beforeEnter: async (to, from, next) => {
            try {
                const lastRoute = from?.fullPath && from.fullPath !== "/" ? from.fullPath : null
                const requestSpecificDisciplineData = !!to?.query?.disciplineId

                await Promise.all([
                    store.dispatch('setLastRouteBeforeDisciplineFeedbacks', lastRoute),
                    store.dispatch('setRenderDisciplinesFeedback', !requestSpecificDisciplineData),
                    store.dispatch('getMaterialAvaliationReasons')
                ])

                const payloadSetup = {
                    page: 1,
                    perPage: 20
                }

                if (requestSpecificDisciplineData) {
                    const disciplineId = to.query.disciplineId

                    store.dispatch('skeleton/setLoadingState', { key: 'disciplineFeedback', value: true })
                    store.dispatch('skeleton/setLoadingState', { key: 'disciplineComments', value: true })
                    store.dispatch('getDisciplineFeedback', { disciplineId: disciplineId, skeletonKey: 'disciplineFeedback' })
                    store.dispatch('getDisciplineComments', { ...payloadSetup, disciplineId: disciplineId, skeletonKey: 'disciplineComments' })
                } else {
                    await store.dispatch('setDisciplineFeedback', null)

                    store.dispatch('skeleton/setLoadingState', { key: 'disciplinesFeedback', value: true })
                    store.dispatch('getDisciplinesFeedback', { ...payloadSetup, skeletonKey: 'disciplinesFeedback' })
                }
                next()
            } catch {
                next(from)
            }
        }
    },
    {
        path: '/social-chat',
        name: 'Chat View',
        component: () => import('@/views/SocialChatView.vue'),
        beforeEnter: async (to, from, next) => {
            try {
                await store.dispatch('getChatUsers', { page: 1, perPage: 10 })

                // Resets the chat state and selects the user carried by the query (?u=id&n=name).
                // Note: beforeEnter does not run again on query-only changes to this same route.
                await store.dispatch('selectChatUserFromQuery', {
                    id: Number(to.query.u) || null,
                    name: to.query.n ? String(to.query.n) : ''
                })
                next()
            } catch {
                next(from)
            }
        }
    },
    {
        path: '/communication/email',
        name: 'Email View',
        component: () => import('@/views/CommunicationEmailView.vue'),
        meta: {
            title: 'Disparos de Email',
            requiresAuth: true
        }
    },
    {
        path: "/academic/organizations",
        name: "Academic Organizations",
        component: () => import("@/views/academic/OrganizationsView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                data: {
                    search: '',
                    status: '',
                    page: 1,
                    perPage: 20
                },
                userToken: userToken
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'organizationsList', value: true })
                store.dispatch('organizations/getOrganizations', { ...payload, skeletonKey: 'organizationsList' })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Organização",
            requiresAuth: true
        }
    },
    {
        path: "/academic/classrooms",
        name: "Academic Classrooms",
        component: () => import("@/views/academic/ClassroomsView.vue"),
        meta: {
            title: "Turma",
            requiresAuth: true
        }
    },
    {
        path: "/academic/periods",
        name: "Academic Periods",
        component: () => import("@/views/academic/PeriodsView.vue"),
        meta: {
            title: "Período Letivo",
            requiresAuth: true,
        },
    },
    {
        path: "/academic/courses",
        name: "Academic Courses",
        component: () => import("@/views/academic/CoursesView.vue"),
        meta: {
            title: "Curso",
            requiresAuth: true,
        },
    },
    {
        path: "/academic/curriculum-matrix",
        name: "Academic Curriculum Matrix",
        component: () => import("@/views/academic/CurriculumMatrixView.vue"),
        meta: {
            title: "Matriz Curricular",
            requiresAuth: true,
        },
    },
    {
        path: "/academic/enrollments",
        name: "Academic Enrollments",
        component: () => import("@/views/academic/EnrollmentsView.vue"),
        meta: {
            title: "Matrícula",
            requiresAuth: true,
        },
    },
    {
        path: "/financial/bank-accounts",
        name: "Financial Bank Accounts",
        component: () => import("@/views/financial/bank-accounts/BankAccountsView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                data: {
                    search: '',
                    page: 1,
                    perPage: 20
                },
                userToken: userToken
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'bankAccountsList', value: true })
                store.dispatch('bankAccounts/getBankAccounts', { ...payload, skeletonKey: 'bankAccountsList' })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Conta Bancária",
            requiresAuth: true
        }
    },
    {
        path: "/financial/payment-methods",
        name: "Financial Payment Methods",
        component: () => import("@/views/financial/payment-methods/PaymentMethodsView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                data: {
                    search: '',
                    bankAccountId: '',
                    page: 1,
                    perPage: 20
                },
                userToken: userToken
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'paymentMethodsList', value: true })
                store.dispatch('paymentMethods/getPaymentMethods', { ...payload, skeletonKey: 'paymentMethodsList' })
                store.dispatch('bankAccounts/getBankAccounts', { data: {}, userToken })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Método de Pagamento",
            requiresAuth: true
        }
    },
    {
        path: "/financial/payment-plans",
        name: "Financial Payment Plans",
        component: () => import("@/views/financial/payment-plans/PaymentPlansView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken
            const payload = {
                data: {
                    search: '',
                    organizationId: store.getters['organizations/getSelectedOrganizationId'] || '',
                    page: 1,
                    perPage: 20
                },
                userToken: userToken
            }

            try {
                store.dispatch('skeleton/setLoadingState', { key: 'paymentPlansList', value: true })
                store.dispatch('paymentPlans/getPaymentPlans', { ...payload, skeletonKey: 'paymentPlansList' })
                store.dispatch('organizations/getOrganizations', { data: { page: 1, perPage: 100 }, userToken })
                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Plano de Pagamento",
            requiresAuth: true
        }
    },
    {
        path: "/financial/student",
        name: "Financial Student",
        component: () => import("@/views/financial/student/StudentFinancialView.vue"),
        beforeEnter: async (to, from, next) => {
            const userToken = store.getters.getToken

            try {
                store.dispatch('installments/selectStudent', { studentId: Number(to.query.sid) || null })

                if (store.getters['installments/getEffectiveStudentId']) {
                    store.dispatch('skeleton/setLoadingState', { key: 'installmentsList', value: true })
                    store.dispatch('skeleton/setLoadingState', { key: 'installmentsStats', value: true })
                    store.dispatch('installments/getInstallments', { userToken, skeletonKey: 'installmentsList' })
                    store.dispatch('installments/getInstallmentsStats', { userToken, skeletonKey: 'installmentsStats' })
                }

                next()
            } catch {
                next(from)
                store.dispatch('toastfy/activeErrorNotification', { text: 'Ocorreu um erro ao acessar a página, tente novamente!' })
            }
        },
        meta: {
            title: "Financeiro",
            requiresAuth: true
        }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'Error',
        component: () => import('@/views/NotFoundView.vue')
    }
];

const requestProfileViewData = async (from, to) => {

    const profileId = to.params.id
    const userId = store.getters.getId
    const userHasAccessedOwnProfile = store.getters.getUserHasAccessedOwnProfile
    const userCheckingOwnProfile = profileId == userId

    if (from.fullPath !== '/' && !userHasAccessedOwnProfile) {
        const lastRouteData = {
            name: from.name,
            params: from.params
        }

        await store.dispatch('setDataRelatedToProfileRoutment', {
            lastRouteAccessed: lastRouteData,
            userHasAccessedOwnProfile: userCheckingOwnProfile
        })
    }

    await store.dispatch('getProfileData', profileId)

    store.dispatch('resetProfileLogs')
    if (userCanViewStudentLogs(store.getters.getType, store.getters.getProfileRole)) {
        store.dispatch('getProfileLogs', { userId: profileId, page: 1 })
    }

    userCheckingOwnProfile ?
        await store.dispatch('getProfileFollowing', profileId)
        : store.dispatch('getProfileFollowing', profileId)

    store.dispatch('getProfileFollowers', profileId)
}

const handleRoutmentExceptions = async (from, to) => {
    if (to.name === 'Profile View') {
        await requestProfileViewData(from, to)
    }
}

const getSubdomain = () => {
    const host = window.location.host;

    return host.split('.')[0];
};

const fetchConsumerSettings = async (subdomain) => {
    try {
        const response = await axios.get(process.env.VUE_APP_APPLICATION_SECURITY + subdomain + '.' + process.env.VUE_APP_ARANDU_API_URL + '/settings');
        return { data: response.data, status: response.status };
    } catch (error) {
        console.error('Erro ao obter as configurações do consumidor:', error);
        return { data: null, status: error.response ? error.response.status : 500 };
    }
};

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

router.beforeEach(async (to, from, next) => {
    const store = useStore();
    const daltonismState = store.getters['acessibility/colorBlindnessState']
    store.dispatch('setLoadingState', { loadignState: true, loadingView: 'app' })
    document.title = to.meta.title || "Orbi Edtech";

    const subdomain = getSubdomain();

    const { data: settings, status } = await fetchConsumerSettings(subdomain);

    if (settings !== null && !daltonismState) {
        store.dispatch('setConsumerSettings', settings);
        await setApplicationColors(settings.settings);
    }

    if (status === 404 || !settings) {
        next({ name: 'License Error' });
    } else {
        await handleRoutmentExceptions(from, to)
        next();
    }
});

router.afterEach(() => {
    setTimeout(() => {
        store.dispatch('setLoadingState', { loadignState: false })
    }, 500);
});

export default router;